// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useMemo, useState } from 'react';
import { Modal, Checkbox, TextInput, useToast } from '@loa/ui-components';
import { profileRequestApi } from '../../api/profileRequestApi';
import { AML_REQUIREMENTS, type AmlDocumentType, type AmlRequirementType } from '../../types/aml';
import type { ProfileRequestDTO } from '../../types/profileRequest';

interface RequestAmlDocumentsModalProps {
  open: boolean;
  onClose: () => void;
  profileId: string;
  targetClientId: string;
  clientName: string;
  /** Called with the created request after a successful send. */
  onCreated?: (request: ProfileRequestDTO) => void;
}

/**
 * "Request AML Documents" modal. Requirements have two shapes (PRD §4,
 * amended 2026-07-26):
 *
 * - Choice groups (Proof of identity / Proof of address): the advisor ticks
 *   WHICH document types are acceptable — the client satisfies the
 *   requirement by uploading ANY ONE of them. One note per group.
 * - Specific documents (source of funds / wealth, tax registration, other):
 *   each tick is its own required upload. OTHER requires a description.
 *
 * Creation goes through the generic Profile Request create with
 * `requestType='AML_DOCUMENTATION'`; the client email fires server-side.
 */
export function RequestAmlDocumentsModal({
  open,
  onClose,
  profileId,
  targetClientId,
  clientName,
  onCreated,
}: Readonly<RequestAmlDocumentsModalProps>) {
  const { showToast } = useToast();
  const [selected, setSelected] = useState<Set<AmlDocumentType>>(new Set());
  const [notes, setNotes] = useState<Partial<Record<AmlRequirementType, string>>>({});
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSelected(new Set());
      setNotes({});
      setSending(false);
      setError(null);
    }
  }, [open]);

  const toggle = (type: AmlDocumentType): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  /** One creation item per requirement with ≥1 ticked document type. */
  const items = useMemo(
    () =>
      AML_REQUIREMENTS.map((req) => {
        const accepted = req.documentTypes
          .filter((d) => selected.has(d.type))
          .map((d) => d.type);
        if (accepted.length === 0) return null;
        return {
          requirementType: req.type,
          acceptedDocumentTypes: accepted,
          note: (notes[req.type] ?? '').trim() || undefined,
        };
      }).filter((i): i is NonNullable<typeof i> => i !== null),
    [selected, notes],
  );

  const canSend = useMemo(() => {
    if (items.length === 0 || sending) return false;
    // OTHER needs the advisor to describe the ask in its note.
    const other = items.find((i) => i.requirementType === 'OTHER');
    if (other && !other.note) return false;
    return true;
  }, [items, sending]);

  const handleSend = async (): Promise<void> => {
    if (!canSend) return;
    setSending(true);
    setError(null);
    try {
      const request = await profileRequestApi.create({
        profileId,
        requestType: 'AML_DOCUMENTATION',
        initiatedByType: 'ADVISOR',
        typeSpecificInput: { targetClientId, items },
      });
      showToast(
        <>
          <strong>AML request</strong> sent to {clientName}.
        </>,
        'success',
      );
      onCreated?.(request);
      onClose();
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      const message = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      if (status === 409) {
        setError('One of the selected requirements is already pending on another AML request for this client.');
      } else {
        setError(message ?? 'Failed to send the request. Please try again.');
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="large" className="fin-modal--compact">
      <Modal.Header icon="shield" title="Request AML Documents" onClose={onClose} />
      <Modal.Content>
        <p className="m-0 mb-md font-sans text-body-03 text-black">
          Select the documents to request from <strong>{clientName}</strong>. They will receive an
          email with an upload link.
        </p>
        {AML_REQUIREMENTS.map((req) => {
          const isChoiceGroup = req.documentTypes.length > 1;
          const selectedCount = req.documentTypes.filter((d) => selected.has(d.type)).length;
          return (
            <div key={req.type} className="mb-md" data-testid={`aml-requirement-${req.type}`}>
              {isChoiceGroup ? (
                <>
                  <div className="font-sans text-body-03 font-medium text-black">{req.label}</div>
                  <div className="font-sans text-body-04 text-secondary-text mb-sm">
                    The client provides any ONE of the selected documents.
                  </div>
                </>
              ) : null}
              <div className="flex flex-col gap-sm">
                {req.documentTypes.map((doc) => (
                  <Checkbox
                    key={doc.type}
                    label={doc.label}
                    checked={selected.has(doc.type)}
                    onChange={() => toggle(doc.type)}
                    disabled={sending}
                    data-testid={`aml-doc-checkbox-${doc.type}`}
                  />
                ))}
                {selectedCount > 0 && (
                  <TextInput
                    fluid
                    placeholder={
                      req.type === 'OTHER'
                        ? 'Describe the document (required)'
                        : 'Note for the client (optional)'
                    }
                    value={notes[req.type] ?? ''}
                    onChange={(e) =>
                      setNotes((prev) => ({ ...prev, [req.type]: e.target.value }))
                    }
                    maxLength={500}
                    disabled={sending}
                    data-testid={`aml-req-note-${req.type}`}
                  />
                )}
              </div>
            </div>
          );
        })}
        {error && (
          <div role="alert" data-testid="aml-request-error" className="font-sans text-body-03 text-error-text">
            {error}
          </div>
        )}
      </Modal.Content>
      <Modal.Actions
        secondaryAction={{ label: 'Cancel', onClick: onClose, disabled: sending }}
        primaryAction={{
          label: items.length > 0 ? `Send request (${items.length})` : 'Send request',
          onClick: () => void handleSend(),
          disabled: !canSend,
          loading: sending,
        }}
      />
    </Modal>
  );
}

export default RequestAmlDocumentsModal;
